export const PRISM_IMAGES = {
  clinicalResults:
    "https://vibe.filesafe.space/1787327131284421342/attachments/edd93631-50de-4588-84f6-ec34c85e96a9.png",
  sessionsTimeline:
    "https://vibe.filesafe.space/1787327131284421342/attachments/2fb569dc-db34-41df-b7d7-ce755fb7236e.png",
  sessionStepByStep:
    "https://vibe.filesafe.space/1787327131284421342/attachments/e0655451-0270-46b7-866a-e4c067276740.png",
  simulationChanges:
    "https://vibe.filesafe.space/1787327131284421342/attachments/9485e5e1-f66b-4f50-86df-bef66c424f7e.png",
  threeDifferences:
    "https://vibe.filesafe.space/1787327131284421342/attachments/b8e31ae0-cfaf-4839-8aed-aa873e1885ee.png",
};

export const PRISM_PHONE = "954-827-9793";
export const PRISM_PHONE_HREF = "tel:9548279793";
export const PRISM_ADDRESS =
  "9734 West Sample Road, 2nd Floor - Suite B, Coral Springs, FL 33065";
export const PRISM_HOURS = "Mon–Fri 7am–7pm · Sat 9am–1pm";
export const PRISM_BOOKING_URL = "/#consultation";

export const mechanismSteps = [
  {
    n: "01",
    title: "The amygdala runs your alarm system.",
    body: "It's a small structure deep in the brain that triggers the fight-flight-freeze response automatically, before conscious thought. In PTSD, this system stays switched on longer and fires more readily than it needs to — which is where many symptoms come from.",
  },
  {
    n: "02",
    title: "Prism can see that activity through an EEG cap.",
    body: "The amygdala sits too deep for EEG to read directly. GrayMatters Health, the company behind Prism, addressed this by recording fMRI and EEG simultaneously and using machine learning to build a digital marker — an EEG signature that tracks amygdala-related activity. This lets a scalp cap stand in for a scanner.",
  },
  {
    n: "03",
    title: "A simulation turns that signal into something you can see.",
    body: "On the monitor, animated characters in a waiting room are agitated — moving, standing, talking over each other. They respond directly to your brain signal. As the amygdala-related activity comes down, the characters settle, sit, and quiet.",
  },
  {
    n: "04",
    title: "You find what brings it down.",
    body: "This is your mental strategy: a memory, an emotion, a place, an experience that makes you feel calm and safe. Everyone's is different, and most people try several before one clicks. The screen tells you immediately whether it's working.",
  },
  {
    n: "05",
    title: "Repetition turns it into a skill.",
    body: "Fifteen sessions of practice, with feedback every few minutes, is training. The goal is that regulating your own state stops being something you concentrate on and becomes something you simply do — in the parking lot, in the crowded room, at that certain time of day.",
  },
];

export const sessionSteps = [
  {
    n: "01",
    title: "Getting set up.",
    body: "A soft EEG cap goes on your head, and one small sensor clips to your earlobe. Your operator applies a water-based gel to each sensor for a clean signal — it wipes off easily afterward. Headphones may be used. Phones and smartwatches go off or into airplane mode.",
  },
  {
    n: "02",
    title: "Calibration.",
    body: "You'll close your eyes for about thirty seconds, then open them at a tone and focus on a marker on the screen. This lets the system take your baseline.",
  },
  {
    n: "03",
    title: "Five cycles.",
    body: "Each cycle runs about four minutes. You'll watch the scene for a minute first, then instructions and a countdown appear, and then you work your mental strategy while the same scene plays. During each session, your Prism operator reviews graphs showing how the brain activity measured during each cycle responded to the mental strategies you tried. After the five cycles, they can discuss which strategy appeared most effective and which one you may want to practice again.",
  },
  {
    n: "04",
    title: "Finishing.",
    body: "Your operator removes the cap and gives you a towel for any residual gel. You may be asked about your experience or to fill out a brief questionnaire. Then you go on with your day — there's no recovery time and nothing to plan around.",
  },
];

export const scheduleRows: [string, string][] = [
  ["Sessions", "15"],
  ["Frequency", "Typically twice weekly"],
  ["Duration", "About 8 weeks"],
  ["Session length", "About 45 minutes"],
  ["Booster sessions", "Available if needed"],
];

export const researchStats = [
  {
    value: "67%",
    label:
      "showed clinically significant improvement in PTSD symptoms (CAPS-5)",
  },
  {
    value: "32%",
    label: "met criteria for remission",
  },
  {
    value: "90%",
    label: "completed the course as prescribed",
  },
  {
    value: "—",
    label: "Improvement was seen across symptom clusters, including sleep",
  },
];

export const faqItems = [
  {
    q: "Do I have to talk about what happened to me?",
    a: "Not for Prism itself. The sessions are non-trauma-based, and your mental strategy is built around what makes you feel calm and safe. Your provider will need enough clinical history to prescribe responsibly, but the treatment doesn't involve recounting the trauma.",
  },
  {
    q: "Does it hurt?",
    a: "No. You'll feel the cap on your head and the gel, which is cool and water-based. Nothing is delivered into your brain.",
  },
  {
    q: "What if I can't find a mental strategy that works?",
    a: "That's common, especially in the first sessions. Finding a useful mental strategy can take exploration. Your Prism operator will guide you through the process and review graphs showing how the brain activity measured during each cycle responded to different strategies. Together, you can identify a strategy to keep practicing. Questionnaires and a Mental Strategy journal also help you and your care team understand changes you notice in daily life.",
  },
  {
    q: "Do I need a prescription?",
    a: "Yes. Prism is a prescription device. Your consultation at NeuroWellness determines whether it's clinically appropriate for you.",
  },
  {
    q: "Is it covered by insurance?",
    a: "Coverage varies. Our team will review your benefits with you and explain your options before you commit to anything.",
  },
  {
    q: "Can I drive myself home?",
    a: "Yes. There's no sedation and no recovery period.",
  },
  {
    q: "How should I prepare?",
    a: "Come with clean hair — no product, no extensions, braids, or ponytails, since these interfere with sensor contact. It also helps to have thought in advance about a memory or experience that makes you feel calm.",
  },
  {
    q: "Is Prism only for PTSD?",
    a: "Prism's FDA clearance is for PTSD, as an add-on to your other care. We also offer a Prism protocol for depression that centers on anhedonia — losing interest, motivation or the ability to enjoy things. That protocol isn't FDA-cleared for depression, and your clinician will talk through whether it makes sense for you.",
  },
  {
    q: "I'm not sad. Could I still have depression?",
    a: "Yes. Some people with depression feel flat, numb or unmotivated rather than sad. If you're getting through your days but not really feeling them, that's worth mentioning at a consultation.",
  },
  {
    q: "I never had one big traumatic event. Can I still have PTSD?",
    a: "PTSD can follow a single event, but it can also build after repeated or prolonged stress, such as first-responder work, military service or a long illness. A clinician can help sort out what fits.",
  },
  {
    q: "My doctor didn't mention Prism. Can I ask about it myself?",
    a: "Yes. You can contact us directly for a consultation. With your permission, we'll coordinate with your current therapist or prescriber so everyone is on the same page.",
  },
];
